import { useCallback, useMemo, useState } from "react";

interface WithId {
    id: string;
    // Ids of every persisted line item a single selectable row stands for. Set
    // when a row collapses multiple lines (e.g. header-level tax/discount/
    // deposit shown as one row); absent for one-to-one rows. Bulk updates expand
    // these so every underlying line is coded, not just the row's own id.
    collapsedLineItemIds?: string[];
}

interface BulkLineItemUpdateParams<TFilters> {
    workspaceId: string;
    classIds?: string[];
    accountId?: string | null;
    accountsReceivableId?: string | null;
    accountsPayableId?: string | null;
    lineItemIds?: string[];
    filters?: TFilters;
}

interface UseLineItemsBulkActionsParams<TFilters> {
    workspaceId?: string | null;
    filters?: TFilters;
    onBulkUpdate: (
        params: BulkLineItemUpdateParams<TFilters>,
    ) => Promise<unknown>;
    onError: (message: string) => void;
    errorMessage?: string;
}

export function useLineItemsBulkActions<TLineItem extends WithId, TFilters>({
    workspaceId,
    filters,
    onBulkUpdate,
    onError,
    errorMessage = "Failed to update line items",
}: UseLineItemsBulkActionsParams<TFilters>) {
    const [selectedLineItems, setSelectedLineItems] = useState<
        Record<string, TLineItem>
    >({});
    const [hasSelectedByCurrentFilters, setHasSelectedByCurrentFilters] =
        useState(false);

    const clearLineItemsSelection = useCallback(() => {
        setSelectedLineItems({});
        setHasSelectedByCurrentFilters(false);
    }, []);

    const handleSelectedLineItemsChange = useCallback(
        (lineItems: TLineItem[]) => {
            const nextSelected = lineItems.reduce<Record<string, TLineItem>>(
                (acc, lineItem) => {
                    acc[lineItem.id] = lineItem;
                    return acc;
                },
                {},
            );
            setSelectedLineItems(nextSelected);
            setHasSelectedByCurrentFilters(false);
        },
        [],
    );

    const selectAllByCurrentFilters = useCallback(() => {
        setSelectedLineItems({});
        setHasSelectedByCurrentFilters(true);
    }, []);

    const handleBulkUpdate = useCallback(
        async (
            update: Pick<
                BulkLineItemUpdateParams<TFilters>,
                | "classIds"
                | "accountId"
                | "accountsReceivableId"
                | "accountsPayableId"
            >,
        ) => {
            if (!workspaceId) {
                return;
            }

            if (
                !hasSelectedByCurrentFilters &&
                Object.keys(selectedLineItems).length === 0
            ) {
                return;
            }

            try {
                await onBulkUpdate({
                    workspaceId,
                    ...update,
                    lineItemIds: hasSelectedByCurrentFilters
                        ? undefined
                        : Object.values(selectedLineItems).flatMap(
                              (lineItem) =>
                                  lineItem.collapsedLineItemIds ?? [
                                      lineItem.id,
                                  ],
                          ),
                    filters: hasSelectedByCurrentFilters ? filters : undefined,
                });
                clearLineItemsSelection();
            } catch {
                onError(errorMessage);
            }
        },
        [
            clearLineItemsSelection,
            errorMessage,
            filters,
            hasSelectedByCurrentFilters,
            onBulkUpdate,
            onError,
            selectedLineItems,
            workspaceId,
        ],
    );

    const handleBulkAccrualAccountSelected = useCallback(
        async (account: { id: string } | null) => {
            await handleBulkUpdate({ accountId: account?.id ?? null });
        },
        [handleBulkUpdate],
    );

    const handleBulkClassesSelected = useCallback(
        async (classIds: string[]) => {
            await handleBulkUpdate({ classIds });
        },
        [handleBulkUpdate],
    );

    const handleBulkAccountsReceivableSelected = useCallback(
        async (account: { id: string } | null) => {
            await handleBulkUpdate({
                accountsReceivableId: account?.id ?? null,
            });
        },
        [handleBulkUpdate],
    );

    const handleBulkAccountsPayableSelected = useCallback(
        async (account: { id: string } | null) => {
            await handleBulkUpdate({
                accountsPayableId: account?.id ?? null,
            });
        },
        [handleBulkUpdate],
    );

    const selectedCount = useMemo(
        () => Object.keys(selectedLineItems).length,
        [selectedLineItems],
    );

    return {
        selectedLineItems,
        hasSelectedByCurrentFilters,
        selectedCount,
        clearLineItemsSelection,
        handleSelectedLineItemsChange,
        selectAllByCurrentFilters,
        handleBulkClassesSelected,
        handleBulkAccrualAccountSelected,
        handleBulkAccountsReceivableSelected,
        handleBulkAccountsPayableSelected,
    };
}
